
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useIntl, FormattedNumber } from "react-intl";
import { useLocation } from "react-router-dom";
import moment from "moment";

//Components
import {
    MenuComponent,
    usePagination,
    TableButtons,
    SearchInput,
    IconButton,
    TableView,
} from "@gpstrackit/gtc-react-controls";
import { Grid } from "@mui/material";

//Actions
import {
    maintenanceDDSDTSearchDriverScheduledTasks,
    reset,
} from './redux/slice';
import {
    openModalCompleteDriverScheduledTask,
    openModalDeleteDriverScheduledTaskRedux,
    openModalScheduleDriverTasks,
} from 'Redux/actions';

//Hooks
import {
    getMaintenanceLabel,
    getMaintenanceValue,
    getMaintenanceBar,
    getPriority,
    statusPill,
} from '../../../../../../core/hooks/utils';

import { usePaggination } from '../../../../../../core/hooks'

//Views
import CompleteDriverScheduledTask from '../../../../modals/completeDriverScheduledTask';
import DeleteDriverScheduledTask from '../../../../modals/deleteDriverScheduledTask';
import ScheduleDriverTask from '../../../../modals/scheduleDriverTask';
import EntityDetails from '../../../../modals/entityDetails';
const ScheduleTasks = (props) => {
    const { refreshTable, view, handleDetails } = props;

    const { messages, locale, formatNumber } = useIntl();
    const dispatch = useDispatch();
    const location = useLocation();
    const tableRef = useRef();

    const pagination = usePaggination(null, null, true, tableRef);

    let path = location.pathname.split("/");
    const id = path[path.length - 1];

    const menuDriverTasksOptions = [
        messages['driversTasksTab_edit'],
        messages['driversTasksTab_complete'],
        messages['driversTasksTab_delete'],
    ];

    //Redux States
    const { loadingScheduledTasks, scheduledTasks, error } = useSelector(state => state.maintenanceScheduleTasksTabRedux);
    const openCompleteDriverScheduledTask = useSelector(state => state.maintenanceCompleteDriverScheduledTaskRedux.open);
    const openDeleteDriverScheduledTask = useSelector(state => state.maintenanceDeleteDriverScheduledTaskRedux.open);
    const openScheduleDriverTasks = useSelector(state => state.maintenanceScheduleDriverTasksRedux.open);
    const openEntityDetails = useSelector(state => state.maintenanceEntityDetailsRedux.open);

    //Local States
    const [driverScheduledTasks, setDriverScheduledTasks] = useState([]);

    useEffect(() => {
        dispatch(maintenanceDDSDTSearchDriverScheduledTasks({ id }))
        return () => {
            pagination.onChangeData([]);
            dispatch(reset());
        }
    }, [])

    useEffect(() => {
        if (error) {
            dispatch(reset({ error: null }))
        }
    }, [error])

    useEffect(() => {
        if (scheduledTasks && scheduledTasks.length) {
            let data = [];
            scheduledTasks.forEach((element) => {
                let tasks = driverScheduledTasks.some((t) => t.id === element.id);
                if (!tasks) data.push(element);
            });
            let currentData = data.sort((a, b) => {
                if (a.status != b.status) {
                    return (
                        (a.status == "Done" && 1) ||
                        (b.status == "Done" && -1) ||
                        new moment(a.dueDate).format("YYYYMMDD") -
                        new moment(b.dueDate).format("YYYYMMDD")
                    );
                } else if (a.status == "Done" && b.status == "Done") {
                    return (
                        new moment(b.lastMaintenanceDate).format("YYYYMMDD") -
                        new moment(a.lastMaintenanceDate).format("YYYYMMDD")
                    );
                } else {
                    return (
                        new moment(a.dueDate).format("YYYYMMDD") -
                        new moment(b.dueDate).format("YYYYMMDD")
                    );
                }
            })
            pagination.onChangeData(currentData);
        } else {
            pagination.onChangeData([]);
        }
    }, [scheduledTasks, loadingScheduledTasks])

    useEffect(() => {
        if (driverScheduledTasks.length || !loadingScheduledTasks) {
            pagination.setColumns([
                {
                    key: "driverTaskName",
                    show: true,
                    title: messages["driversTasksTab_task"],
                    export: true,
                    sort: true,
                    render: (row) => {
                        return (
                            <Grid container alignItems="center">
                                <Grid item xs={9}>
                                    <span>{row?.driverTaskName}</span>
                                </Grid>
                                <Grid item xs={3}>
                                    <MenuComponent
                                        isDisable={loadingScheduledTasks}
                                        options={menuDriverTasksOptions}
                                        className="showInHover"
                                        onClick={(type, value) => {
                                            if (type === messages["driversTasksTab_edit"]) {
                                                dispatch(openModalScheduleDriverTasks({ open: true, id: row.id }))
                                            } else if (type === messages["driversTasksTab_complete"]) {
                                                dispatch(openModalCompleteDriverScheduledTask({ open: true, tasks: row }))
                                            } else if (type === messages["driversTasksTab_delete"]) {
                                                dispatch(openModalDeleteDriverScheduledTaskRedux({ open: true, tasks: row }))
                                            }
                                        }}
                                        value={row}
                                        icon="more_vert"
                                    />
                                </Grid>
                            </Grid>
                        );
                    },
                },
                {
                    key: "Due",
                    show: true,
                    title: messages["driversTasksTab_due"],
                    export: true,
                    sort: true,
                    render: (row) => {
                        if (row.createdOn && row.frequency) {
                            return (
                                <>
                                    {getMaintenanceBar("time", row.status, row.lastMaintenanceDate || row.createdOn, row.dueDate, moment(), messages)}
                                </>
                            );
                        }
                    },
                    sortValue: (row) => {
                        if (!row.createdOn || !row.frequency) {
                            return 0;
                        }
                        const percentage = getMaintenanceValue("time", row.lastMaintenanceDate || row.createdOn, row.dueDate, moment())
                        return percentage;
                    },
                },
                {
                    key: "estimatedCost",
                    show: true,
                    title: messages["driversTasksTab_cost"],
                    export: true,
                    sort: true,
                    render: (row) => {
                        return row.estimatedCost ? (
                            <FormattedNumber
                                value={row.estimatedCost}
                                style="currency"
                                currency="USD"
                            />
                        ) : <></>
                    },
                    sortValue: (row) => {
                        const format = { style: 'currency', currency: 'USD' };
                        const numberFormat = new Intl.NumberFormat('en-US', format);
                        return row?.estimatedCost ? numberFormat.format(row?.estimatedCost) : '0';
                    },
                },
                {
                    key: "status",
                    show: true,
                    title: messages["driversTasksTab_status"],
                    export: true,
                    sort: true,
                    render: (row) => {
                        if (row.status) {
                            return <>{statusPill(row)}</>;
                        }
                    },
                    sortValues: (row) => {
                        return getPriority(row.status)
                    }
                },
                {
                    key: "subscribers",
                    show: true,
                    title: messages["driversTasksTab_subscribers"],
                    export: true,
                    render: (row) => {
                        const { subscribersCount, id } = row;
                        return (
                            <>
                                {subscribersCount > 0 ? (
                                    <IconButton
                                        placement="bottom"
                                        variant="secondary"
                                        icon="people"
                                        size="small"
                                        disabled={loadingScheduledTasks}
                                        onClick={() => handleDetails(id, 'DriverScheduledTask', 0, row.driverTaskName)}
                                    />
                                ) : ""}
                            </>
                        );
                    }
                },
                {
                    key: "notes",
                    show: true,
                    title: messages["driversTasksTab_notes"],
                    export: true,
                    render: (row) => {
                        if (row.notes && row.id) {
                            const { notes, id } = row;
                            return (
                                <>
                                    {notes ? (
                                        <IconButton
                                            placement="bottom"
                                            variant="secondary"
                                            icon="comment"
                                            size="small"
                                            disabled={loadingScheduledTasks}
                                            onClick={() => handleDetails(id, 'DriverScheduledTask', 1, row.driverTaskName)}
                                        />
                                    ) : ""}
                                </>
                            );
                        }
                    }
                },
                {
                    key: "attachments",
                    show: true,
                    title: messages["driversTasksTab_attachments"],
                    export: true,
                    render: (row) => {
                        if (row.fileIds && row.id) {
                            const { fileIds, id } = row;
                            return (
                                <>
                                    {fileIds && fileIds.length > 0 ? (
                                        <IconButton
                                            placement="bottom"
                                            variant="secondary"
                                            icon="attach_file"
                                            size="small"
                                            disabled={loadingScheduledTasks}
                                            onClick={() => handleDetails(id, 'DriverScheduledTask', 2, row.driverTaskName)}
                                        />
                                    ) : ""}
                                </>
                            );
                        }
                    }
                }
            ]);
        }
    }, [driverScheduledTasks, locale])

    const refreshGrid = () => {
        dispatch(maintenanceDDSDTSearchDriverScheduledTasks({ id }))
    }

    const getDataToExport = () => {
        let values = []
        let allData = pagination?.allData

        if (allData) {
            allData.forEach((item) => {
                let driverTaskName = item?.driverTaskName
                let due = getMaintenanceLabel("time", item.lastMaintenanceDate || item.createdOn, item.dueDate, moment(), messages)
                let estimatedCost = formatNumber(item.estimatedCost || 0, { style: "currency", currency: "USD" }).replace(/,/g, '.');
                let status = item.status
                let subscribersCount = item?.subscribersCount || ""
                let notes = item?.notes || ""
                let fileIds = item.fileIds.length || ""
                values.push([driverTaskName, due, estimatedCost, status, subscribersCount, notes, fileIds])
            })
        }
        return values
    }

    return (
        <>
            <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                spacing={1}
            >
                <Grid item xs={6}>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item style={{ display: 'flex', paddingTop: 0 }}>
                            <IconButton
                                icon="event"
                                style={{ padding: 5, marginRight: 5 }}
                                className={view === 'scheduleTask' ? 'selected-driver-detail' : ''}
                                onClick={() => refreshTable("scheduleTask")}
                                tooltip={messages["driver_detail_schedule_tasks"]}
                            />
                            <IconButton
                                icon="check_circle"
                                style={{ padding: 5, marginRight: 5 }}
                                className={view === 'scheduleTaskLog' ? 'selected-driver-detail' : ''}
                                onClick={() => refreshTable("scheduleTaskLog")}
                                tooltip={messages["driver_detail_schedule_tasks_logs"]}
                            />
                            <TableButtons
                                tableRef={tableRef}
                                buttons={["export"]}
                                onExport={(type) => {
                                    if (type === "Csv") tableRef.current?.exportCSV()
                                    else tableRef.current?.exportPDF()
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={6} style={{ paddingTop: 5 }} className="mb-10 text-right">
                    <SearchInput
                        placeholder={messages['search']}
                        searchText={pagination.searchText}
                        onSearchText={pagination.onSearchChange}
                    />
                </Grid>
            </Grid>
            <TableView
                ref={tableRef}
                loading={loadingScheduledTasks}
                hideDragable
                keyColumn="id"
                hideAllInPagination
                disableItemSelection
                topheight={220}
                topSpaceSize={265}
                {...pagination}
                getDataToExport={(data) => getDataToExport(data)}
                exportName="schedule_tasks"
                exportOrientation="landscape"
            />

            {/* Views */}
            {openCompleteDriverScheduledTask && <CompleteDriverScheduledTask refresh={() => refreshGrid()} />}
            {openDeleteDriverScheduledTask && <DeleteDriverScheduledTask refresh={() => refreshGrid()} />}
            {openScheduleDriverTasks && <ScheduleDriverTask refresh={() => refreshGrid()} />}
            {openEntityDetails && <EntityDetails refresh={() => refreshGrid()} />}
        </>
    )
}
export default ScheduleTasks;